import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { UniversalWasteCategory, WasteKind, WillNotReportSource } from "generated-graphql/graphql";
import { prettyPrintWasteClassification } from "routes/Customer/Waste/Facilities/Facility/prettyPrintWasteClassification";

import { ExplainableWasteLine, ExplainedField, WasteClassificationExplanation } from "./types";

/**
 * How the line came to carry a value the rules don't produce. `manual` means
 * a person set it (today only the reportability value can carry that);
 * `vendor` means it came through from the vendor's data; `record` means
 * nobody did, so the value on the record predates the rules as they stand today.
 */
export type DifferenceOrigin = "manual" | "vendor" | "record";

/**
 * One value this line carries that the compliance engine doesn't produce,
 * already written out for a reader. `currently` and `wouldBe` are display
 * phrases, never raw enum values.
 */
export type DifferenceRow = {
  origin: DifferenceOrigin;
  /** What this line is today, e.g. "Universal Waste". */
  currently: string;
  /** What today's rules would make it, e.g. "RCRA Hazardous". */
  wouldBe: string;
};

/** The line's differing values, keyed by the engine field whose verdict each renders beside. */
export type Differences = Partial<Record<ExplainedField, DifferenceRow>>;

/**
 * The second sentence of the note: how the current value got there. Only a
 * person putting it there is worth naming — a value that simply predates the
 * rules says nothing a reader can act on, so it gets no sentence.
 */
const ORIGIN_SENTENCE: Record<DifferenceOrigin, string> = {
  manual: " Someone set this by hand.",
  vendor: " This came from the vendor's data.",
  record: "",
};

function sentenceCase(phrase: string): string {
  return phrase.charAt(0).toUpperCase() + phrase.slice(1);
}

/** The chip beside a verdict: what this line is today. */
export function differenceChipLabel(row: DifferenceRow): string {
  return `Currently: ${sentenceCase(row.currently)}`;
}

/** The tooltip on a differing verdict: what the line is, then what the rules say. */
export function differenceNote(row: DifferenceRow | undefined): string | null {
  if (!row) return null;
  return (
    `This line is currently ${row.currently}. Today's rules would make it ` +
    `${row.wouldBe}.${ORIGIN_SENTENCE[row.origin]}`
  );
}

/**
 * Where the line's own reportability value came from. VENDOR and MANUAL are
 * both someone's decision, at different points; anything else means the value
 * was derived once and has not kept up with the rules.
 */
function reportabilityOrigin(source: WillNotReportSource | null | undefined): DifferenceOrigin {
  if (source === WillNotReportSource.Vendor) return "vendor";
  if (source === WillNotReportSource.Manual) return "manual";
  return "record";
}

/**
 * `willNotReport` is stored inverted, which is exactly the kind of detail a
 * reader should never have to hold. Both phrases name the report.
 */
function biennialWording(willNotReport: boolean): string {
  return willNotReport ? "not on the federal biennial report" : "on the federal biennial report";
}

/** From the rules, a null category is the real answer "this is not universal waste". */
function universalWasteCategoryWording(value: UniversalWasteCategory | null | undefined): string {
  return value == null ? "not universal waste" : prettyPrintEnumValue(value);
}

/**
 * On the line, a null category means "not universal waste" only when its
 * stream isn't one: a universal waste stream's lines can lack a category.
 */
function storedUniversalWasteCategoryWording(
  value: UniversalWasteCategory | null | undefined,
  streamWasteKind: WasteKind | null | undefined
): string {
  return value == null && streamWasteKind === WasteKind.UniversalWaste
    ? "universal waste with no category recorded"
    : universalWasteCategoryWording(value);
}

/** The verdict's own display value: sentence-cased, so a null category reads "Not universal waste". */
export function universalWasteCategoryValue(
  value: UniversalWasteCategory | null | undefined
): string {
  return sentenceCase(universalWasteCategoryWording(value));
}

/**
 * Compare what the line carries against what the rules produce for it today.
 * `result` is already override-free, so every entry is a real disagreement.
 * A null stored classification means nobody set one, so it is skipped; the
 * other two compare null as a value.
 */
export function computeDifferences(
  { result, streamWasteKind }: Pick<WasteClassificationExplanation, "result" | "streamWasteKind">,
  line: ExplainableWasteLine
): Differences {
  const differences: Differences = {};

  const vendorClassification = line.vendorClassification;
  if (vendorClassification != null && vendorClassification !== result.classification) {
    differences.classification = {
      origin: "vendor",
      currently: prettyPrintWasteClassification(vendorClassification),
      wouldBe: prettyPrintWasteClassification(result.classification),
    };
  }

  const willNotReport = line.willNotReport ?? false;
  if (willNotReport !== result.willNotReport) {
    differences.reportableOnFederalBiennial = {
      origin: reportabilityOrigin(line.willNotReportSource),
      currently: biennialWording(willNotReport),
      wouldBe: biennialWording(result.willNotReport),
    };
  }

  if ((line.universalWasteCategory ?? null) !== result.universalWasteCategory) {
    differences.universalWasteGrant = {
      origin: "record",
      currently: storedUniversalWasteCategoryWording(line.universalWasteCategory, streamWasteKind),
      wouldBe: universalWasteCategoryWording(result.universalWasteCategory),
    };
  }

  return differences;
}
